import React, { useEffect, useRef, useState } from "react";

const DEFAULT_DESMOS_API_KEY = "12345";
const DESMOS_API_VERSION = "1.11";

let desmosLoader = null;
let desmosLoaderSrc = null;

export const resolveDesmosApiKey = (apiKey) => {
  const envKey = process.env.REACT_APP_DESMOS_API_KEY || "";
  const key = String(apiKey || envKey || "").trim();
  return {
    key: key || DEFAULT_DESMOS_API_KEY,
    isMissing: !key || key === DEFAULT_DESMOS_API_KEY,
  };
};

export const getDesmosScriptUrl = (apiKey) => {
  const { key } = resolveDesmosApiKey(apiKey);
  return `https://www.desmos.com/api/v${DESMOS_API_VERSION}/calculator.js?apiKey=${encodeURIComponent(
    key
  )}`;
};

const loadDesmos = (apiKey) => {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.Desmos) return Promise.resolve(window.Desmos);
  const src = getDesmosScriptUrl(apiKey);
  if (desmosLoader && desmosLoaderSrc === src) return desmosLoader;
  desmosLoaderSrc = src;
  desmosLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve(window.Desmos);
    script.onerror = () => reject(new Error("Failed to load Desmos"));
    document.head.appendChild(script);
  });
  return desmosLoader;
};

const syncExpressions = (calculator, managedIdsRef, expressions) => {
  if (!calculator) return;
  const nextExpressions = Array.isArray(expressions) ? expressions : [];
  const nextIds = new Set();
  nextExpressions.forEach((expression) => {
    if (!expression || !expression.id) return;
    nextIds.add(expression.id);
    calculator.setExpression(expression);
  });

  managedIdsRef.current.forEach((id) => {
    if (!nextIds.has(id)) {
      calculator.removeExpression({ id });
    }
  });
  managedIdsRef.current = nextIds;
};

const DesmosGraph = ({
  expressions = [],
  height = 420,
  className = "",
  testId = "desmos-graph",
  apiKey,
}) => {
  const containerRef = useRef(null);
  const calculatorRef = useRef(null);
  const managedIdsRef = useRef(new Set());
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    const { key, isMissing } = resolveDesmosApiKey(apiKey);
    if (isMissing) {
      setStatus("missing-key");
      return () => {
        active = false;
      };
    }
    loadDesmos(key)
      .then((Desmos) => {
        if (!active || !containerRef.current) return;
        if (!calculatorRef.current) {
          calculatorRef.current = Desmos.GraphingCalculator(
            containerRef.current,
            {
              expressions: true,
              settingsMenu: true,
              keypad: true,
              zoomButtons: true,
              border: false,
            }
          );
        }
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    return () => {
      active = false;
      if (calculatorRef.current) {
        calculatorRef.current.destroy();
        calculatorRef.current = null;
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!calculatorRef.current || status !== "ready") return;
    syncExpressions(calculatorRef.current, managedIdsRef, expressions);
  }, [expressions, status]);

  return (
    <div
      className={`desmos-graph${className ? ` ${className}` : ""}`}
      data-testid={testId}
    >
      {status === "error" ? (
        <p className="mini-label" style={{ margin: 0 }}>
          Graph unavailable.
        </p>
      ) : status === "missing-key" ? (
        <p className="mini-label" style={{ margin: 0 }}>
          Desmos API key missing. Set REACT_APP_DESMOS_API_KEY and restart.
        </p>
      ) : (
        <>
          {status !== "ready" && (
            <p className="mini-label" style={{ margin: 0 }}>
              Loading graph…
            </p>
          )}
          <div
            ref={containerRef}
            className="desmos-graph__canvas"
            style={
              typeof height === "number"
                ? { minHeight: `${height}px` }
                : typeof height === "string"
                ? { minHeight: height }
                : undefined
            }
            aria-busy={status !== "ready"}
          />
        </>
      )}
    </div>
  );
};

export default DesmosGraph;
